import {
    useQuery,
    useMutation,
    QueryClient,
    QueryResult,
    UseMutationResult,
    QueryOptions,
} from '@eika-infrastruktur/react-query';
import { RequestExceptionWithResponse } from '@eika-infrastruktur/request';

import { SelectedReceiver } from '../components/transaction/SelectedReceiver';
import type { AccountNumber, ImmediatePaymentInfo, OcrInformation } from '../generated-types';
import { fetchImmediatePaymentInfoForCreditor, fetchOcrInformation } from '../services/ocrService';

const useOcr = (
    accountNumber?: AccountNumber,
    enabled = true,
    options: Partial<QueryOptions<OcrInformation>> = {},
): QueryResult<OcrInformation> =>
    useQuery({
        ...options,
        queryKey: ['ocr', accountNumber?.value],
        queryFn: () => fetchOcrInformation(accountNumber!),
        enabled: !!accountNumber && enabled,
        staleTime: Infinity,
    });

export const useOcrCallback = (
    accountNumber?: AccountNumber,
    onSuccess?: (info: OcrInformation) => void,
    enabled = true,
): void => {
    useOcr(accountNumber, enabled, { onSuccess });
};

export const useOcrMutation = (
    successCallback?: (data: OcrInformation, variables: SelectedReceiver) => void,
    errorCallback?: () => void,
): UseMutationResult<OcrInformation, RequestExceptionWithResponse, SelectedReceiver> =>
    useMutation({
        mutationFn: (receiver) => fetchOcrInformation(receiver.accountNumber),
        onSuccess: successCallback,
        onError: errorCallback,
    });

const getImmediatePaymentCreditorKey = (creditor: AccountNumber | undefined, debitor: AccountNumber | undefined) => {
    return ['ocr', 'immediate', creditor?.value, debitor?.value];
};

export const ensureImmediatePaymentCreditorInfo =
    (queryClient: QueryClient, enabled: boolean) =>
    async (
        creditor: AccountNumber | undefined,
        debitor: AccountNumber | undefined,
        immediate: boolean | undefined,
    ): Promise<ImmediatePaymentInfo | undefined> => {
        if (!immediate || !enabled || !creditor || !debitor) return Promise.resolve(undefined);
        try {
            return await queryClient.ensureQueryData<ImmediatePaymentInfo>({
                queryKey: getImmediatePaymentCreditorKey(creditor, debitor),
            });
        } catch {
            return Promise.resolve(undefined);
        }
    };

export const useImmediatePaymentCreditor = (
    creditor: AccountNumber | undefined,
    debitor: AccountNumber | undefined,
    enabled = true,
): QueryResult<ImmediatePaymentInfo> =>
    useQuery({
        queryKey: getImmediatePaymentCreditorKey(creditor, debitor),
        queryFn: () => fetchImmediatePaymentInfoForCreditor(creditor!, debitor!),
        enabled: !!creditor && !!debitor && enabled,
    });

export default useOcr;
